"use client";

import React from "react";
import { cn } from "@/lib/utils";

interface AuthSideSectionProps {
  className?: string;
}

export function AuthSideSection({ className }: AuthSideSectionProps) {
  return (
    <div
      className={cn(
        "w-1/2 bg-gradient-to-br from-purple-100 via-primary/8 to-blue-50 relative overflow-hidden hidden md:flex",
        "dark:from-primary/20 dark:via-primary/10 dark:to-background",
        className,
      )}
    >
      {/* Multiple gradient layers for depth - LIGHT MODE ONLY */}
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-primary/10 dark:opacity-0" />
      <div className="absolute inset-0 bg-gradient-to-bl from-primary/15 via-transparent to-purple-100/50 dark:opacity-0" />

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.08] dark:opacity-[0.05]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%235659ff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center items-start p-6 lg:p-8 xl:p-12 max-w-xl min-h-screen">
        {/* Logo and Brand */}
        <div className="flex items-center gap-3 mb-6 lg:mb-8">
          <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl overflow-hidden bg-primary/10 backdrop-blur-sm border border-primary/20 dark:bg-primary/20 dark:border-primary/30">
            <img
              src="/assets/auth/logo.png"
              alt="Shiphouse Logo"
              width={56}
              height={56}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-foreground">
              Shiphouse
            </h1>
            <p className="text-slate-600 dark:text-muted-foreground text-xs lg:text-sm">
              Drop Shipping Platform
            </p>
          </div>
        </div>

        {/* Main Headline */}
        <div className="mb-8 lg:mb-10">
          <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-slate-900 dark:text-foreground leading-tight mb-3 lg:mb-4">
            Scale Your
            <span className="block text-transparent bg-gradient-to-r from-primary to-primary/70 bg-clip-text">
              Dropshipping Empire
            </span>
          </h2>
          <p className="text-slate-600 dark:text-muted-foreground text-sm lg:text-base leading-relaxed">
            Join thousands of successful entrepreneurs who trust Shiphouse to
            power their e-commerce dreams.
          </p>
        </div>

        {/* Features - Compact Grid */}
        <div className="grid grid-cols-1 gap-3 lg:gap-4 w-full">
          <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-xl p-3 lg:p-4 border border-primary/10 hover:border-primary/20 hover:bg-white hover:shadow-sm transition-all duration-300 dark:bg-card/30 dark:hover:bg-card/50 dark:border-border">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-primary/15 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 lg:w-5 lg:h-5 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-slate-900 dark:text-foreground font-semibold text-sm lg:text-base mb-0.5">
                Lightning Fast Setup
              </h3>
              <p className="text-slate-600 dark:text-muted-foreground text-xs lg:text-sm leading-tight">
                Get your store running in minutes
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-xl p-3 lg:p-4 border border-primary/10 hover:border-primary/20 hover:bg-white hover:shadow-sm transition-all duration-300 dark:bg-card/30 dark:hover:bg-card/50 dark:border-border">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-primary/15 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 lg:w-5 lg:h-5 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 5a2 2 0 012-2h4a2 2 0 012 2v1H8V5z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-slate-900 dark:text-foreground font-semibold text-sm lg:text-base mb-0.5">
                Premium Products
              </h3>
              <p className="text-slate-600 dark:text-muted-foreground text-xs lg:text-sm leading-tight">
                Thousands of winning products
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-xl p-3 lg:p-4 border border-primary/10 hover:border-primary/20 hover:bg-white hover:shadow-sm transition-all duration-300 dark:bg-card/30 dark:hover:bg-card/50 dark:border-border">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-primary/15 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 lg:w-5 lg:h-5 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-slate-900 dark:text-foreground font-semibold text-sm lg:text-base mb-0.5">
                Advanced Analytics
              </h3>
              <p className="text-slate-600 dark:text-muted-foreground text-xs lg:text-sm leading-tight">
                Data-driven insights
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between gap-4 mt-6 lg:mt-8 pt-6 lg:pt-8 border-t border-primary/10 dark:border-border w-full">
          <div className="text-center">
            <div className="text-lg lg:text-xl font-bold text-slate-900 dark:text-foreground">
              50K+
            </div>
            <div className="text-slate-600 dark:text-muted-foreground text-xs lg:text-sm">
              Sellers
            </div>
          </div>
          <div className="text-center">
            <div className="text-lg lg:text-xl font-bold text-slate-900 dark:text-foreground">
              $2M+
            </div>
            <div className="text-slate-600 dark:text-muted-foreground text-xs lg:text-sm">
              Revenue
            </div>
          </div>
          <div className="text-center">
            <div className="text-lg lg:text-xl font-bold text-slate-900 dark:text-foreground">
              99.9%
            </div>
            <div className="text-slate-600 dark:text-muted-foreground text-xs lg:text-sm">
              Uptime
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements - Much more vibrant and numerous */}
      {/* Top right area */}
      <div className="absolute top-10 right-10 w-40 h-40 bg-gradient-to-br from-primary/30 to-white/90 dark:from-primary/10 dark:to-primary/5 rounded-full blur-3xl" />
      <div className="absolute top-32 right-32 w-32 h-32 bg-gradient-to-br from-purple-200/60 to-primary/25 dark:from-primary/8 dark:to-primary/4 rounded-full blur-2xl" />

      {/* Bottom right area */}
      <div className="absolute bottom-20 right-12 w-36 h-36 bg-gradient-to-br from-white to-primary/35 dark:from-primary/15 dark:to-primary/8 rounded-full blur-3xl" />
      <div className="absolute bottom-40 right-24 w-24 h-24 bg-gradient-to-br from-blue-100/70 to-primary/20 dark:from-primary/10 dark:to-primary/5 rounded-full blur-2xl" />

      {/* Center area */}
      <div className="absolute top-1/2 right-1/3 w-28 h-28 bg-gradient-to-br from-primary/40 to-white/80 dark:from-primary/20 dark:to-primary/10 rounded-full blur-2xl" />
      <div className="absolute top-1/3 right-1/2 w-20 h-20 bg-gradient-to-br from-white/90 to-purple-200/50 dark:from-primary/12 dark:to-primary/6 rounded-full blur-xl" />

      {/* Left side area */}
      <div className="absolute top-1/4 left-8 w-32 h-32 bg-gradient-to-br from-white/95 to-primary/20 dark:from-primary/8 dark:to-primary/4 rounded-full blur-3xl" />
      <div className="absolute bottom-1/3 left-16 w-36 h-36 bg-gradient-to-br from-primary/25 to-white dark:from-primary/12 dark:to-primary/6 rounded-full blur-3xl" />
      <div className="absolute top-2/3 left-24 w-24 h-24 bg-gradient-to-br from-purple-100/80 to-primary/15 dark:from-primary/10 dark:to-primary/5 rounded-full blur-2xl" />
    </div>
  );
}
